// src/firebaseConfig.js
/* eslint-disable no-undef */
import { initializeApp, getApps } from "firebase/app";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

/**
 * Firebase config
 * NOTE: This file must export `app` so Functions can bind correctly:
 *   import { app } from "../firebaseConfig";
 */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyAHH-6KMVd5REfZw1ShuVwDqozn6-KlYyA",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "church-fundraising-rccg.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "church-fundraising-rccg",
  // Keep as-is if that's what your console shows
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "church-fundraising-rccg.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "1090365542928",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:1090365542928:web:b2510c3cbe356f71e4f095",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-L57DD8G75S",
};

// ✅ Avoid duplicate init in hot-reload
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// Observe-first App Check rollout. A token provider is initialized only when a
// release supplies the public Enterprise site key. Server enforcement remains
// independently disabled until compatibility telemetry proves every supported
// browser/channel is attaching valid tokens.
const appCheckSiteKey = String(
  process.env.REACT_APP_FIREBASE_APPCHECK_ENTERPRISE_SITE_KEY || "",
).trim();
const appCheck = appCheckSiteKey
  ? initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    })
  : null;

// ✅ Core services
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// ✅ Functions region must match your deployed functions
const FUNCTIONS_REGION = "us-central1";
const functions = getFunctions(app, FUNCTIONS_REGION);

/**
 * ✅ Emulator routing (DEV ONLY)
 * - Works on localhost and 127.0.0.1
 * - Avoids "already connected" crash during hot reload
 */
const isBrowser = typeof window !== "undefined";
const isLocalhost =
  isBrowser &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

// Local visual testing should read the real Firebase project by default.
// Set REACT_APP_USE_FIREBASE_EMULATORS=true to opt into local emulators.
const emulatorFlag =
  String(process.env.REACT_APP_USE_FIREBASE_EMULATORS ?? "")
    .trim()
    .toLowerCase() === "true";
const shouldUseFirebaseEmulators = isLocalhost && emulatorFlag;

const FUNCTIONS_PROJECT_ID =
  String(process.env.REACT_APP_FIREBASE_PROJECT_ID || firebaseConfig.projectId || "")
    .trim() || firebaseConfig.projectId;
const configuredFunctionsBaseUrl =
  String(process.env.REACT_APP_FUNCTIONS_BASE_URL || "").trim();

function httpsFunctionUrl(functionName) {
  const safeFunctionName = String(functionName || "").trim().replace(/^\/+/, "");
  const baseUrl = configuredFunctionsBaseUrl || (
    shouldUseFirebaseEmulators
      ? `http://127.0.0.1:5001/${FUNCTIONS_PROJECT_ID}/${FUNCTIONS_REGION}`
      : `https://${FUNCTIONS_REGION}-${FUNCTIONS_PROJECT_ID}.cloudfunctions.net`
  );
  return `${baseUrl.replace(/\/+$/, "")}/${safeFunctionName}`;
}

if (shouldUseFirebaseEmulators) {
  // These flags prevent re-connecting on hot reload
  if (!globalThis.__FIREBASE_EMULATORS_CONNECTED__) {
    globalThis.__FIREBASE_EMULATORS_CONNECTED__ = true;

    // eslint-disable-next-line no-console
    console.log("Running locally: Connecting to Firebase Emulators...");

    // Auth
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });

    // Firestore
    connectFirestoreEmulator(db, "127.0.0.1", 8080);

    // Functions
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);

    // Storage
    connectStorageEmulator(storage, "127.0.0.1", 9199);
  }
}

/**
 * ✅ IMPORTANT FOR WEB APP SIGN-IN
 * If you ever see "auth/unauthorized-domain" on production,
 * add your deployed domain(s) in:
 * Firebase Console → Authentication → Settings → Authorized domains.
 */

export { app, appCheck, auth, db, storage, functions, shouldUseFirebaseEmulators, httpsFunctionUrl };
